import { z } from 'zod';
import { MONEY_PATTERN } from '@miraki/money';
import { isPositiveAmount } from './primitives.js';

/**
 * A-41 subscription writes — SB-01, SB-02.
 *
 * These live here, not in the API, because the web form and the route must agree on them. They
 * were written in `apps/api/src/subscription/subscription-input.ts` only because the contracts
 * barrel was single-writer while A-41 was being built, and TS1 asked for the move: the
 * Subscriptions screen was restating the shapes by hand, which is two definitions of one rule
 * and the usual way a form and its endpoint drift apart.
 *
 * The pure checks over a stored term — `cannotCancel`, `cannotMarkPaid`, `overlapping` — stay
 * in the API, because they read a database row rather than a request body.
 */

const zDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be a date, YYYY-MM-DD');

/** A decimal string, strictly positive. Money is never a number (NF-04, invariant 1). */
/*
 * The positivity rule comes from `primitives.ts` rather than being restated here. It was
 * restated, and the approval path then used plain `zMoney` and accepted a zero fee — one field,
 * two answers. This keeps the string output A-41's store expects while sharing the rule.
 */
const zFee = z.string().regex(MONEY_PATTERN, 'must be a decimal amount')
  .refine(isPositiveAmount, 'must be greater than zero');

/**
 * SB-02 — record a term. `feeReference` with the term means the fee was paid when it was
 * recorded, which is the usual AUS case: SB-01 says the fee is paid BEFORE the account is set up.
 */
export const zRecordTerm = z.object({
  termStart: zDate,
  termEnd: zDate,
  fee: zFee,
  paid: z.boolean().default(false),
  feeReference: z.string().trim().min(1).max(120).optional(),
}).refine((v) => v.termEnd > v.termStart, { message: 'the renewal date must be after the start date', path: ['termEnd'] })
  .refine((v) => !v.paid || v.feeReference !== undefined, {
    message: 'a paid fee needs the bank reference or receipt number it was paid with', path: ['feeReference'],
  });
export type RecordTerm = z.infer<typeof zRecordTerm>;

/** SB-02 — "marks each year's fee as paid". Offline (SB-01), so the reference is what was given. */
export const zMarkPaid = z.object({ feeReference: z.string().trim().min(1).max(120) });
export type MarkPaid = z.infer<typeof zMarkPaid>;

/** SB-02's CANCELLED. A reason is required: SB-05 keeps changes, and a cancellation is the one that matters. */
export const zCancelTerm = z.object({ reason: z.string().trim().min(3).max(500) });
export type CancelTerm = z.infer<typeof zCancelTerm>;
